import { Dialog, Transition } from "@headlessui/react";
import classNames from "classnames";
import React, { Fragment, useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { DialogEnum, useDialog } from "../../contexts/DialogContext";
import {
  ConfirmationCodeFormValues,
  useConfirmationCodeForm,
} from "../../forms/auth/confirmation-code-form";
import { CognitoService } from "../../services/cognito-service";

export default function ConfirmAccountDialog() {
  const { confirmAccount, usernameToConfirm } = useAuth();
  const dialog = useDialog();
  const navigate = useNavigate();
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const { register, handleSubmit, formState } = useConfirmationCodeForm();

  const onSubmit = async (values: ConfirmationCodeFormValues) => {
    await toast.promise(
      confirmAccount(values.code, usernameToConfirm),
      {
        loading: "Saving...",
        success: () => {
          dialog.pop();
          navigate("/");
          return <b>Successful confirmation!</b>;
        },
        error: (e) => {
          return e?.response
            ? e.response.data
            : e ?? "An Error Occurred. Verify you entered the correct verification code.";
        },
      },
      { duration: 5000 }
    );
  };

  const resendCode = async () => {
    try {
      await CognitoService.resendConfirmationCode(usernameToConfirm);
      setTimeLeft(59);
    } catch (e) {
      toast.error(e as string);
    }
  };

  useEffect(() => {
    if (!timeLeft) return;

    const intervalId = setInterval(() => {
      setTimeLeft(timeLeft - 1);
    }, 1000);

    return () => clearInterval(intervalId);
  }, [timeLeft]);

  return (
    <Transition
      appear
      show={dialog.isOpened(DialogEnum.CONFIRM_REGISTRATION)}
      as={Fragment}
    >
      <Dialog
        as="div"
        className="fixed inset-0 z-10 overflow-y-auto"
        onClose={() => { }}
      >
        <div className="min-h-screen text-center">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-700"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-500"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 transition-opacity bg-black bg-opacity-30" />
          </Transition.Child>

          <span
            className="inline-block h-screen align-middle"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <div className="inline-block w-full max-w-md p-10 overflow-hidden text-left align-middle transition-all transform bg-white rounded-md shadow-xl">
              <form onSubmit={handleSubmit(onSubmit)}>
                <Dialog.Title className="text-2xl font-semibold tracking-wide uppercase text-primary-blue">
                  Verification code
                </Dialog.Title>
                <div className="mt-4">
                  <p className="text-sm text-gray-500">
                    Registration completed successfully. Please check your email
                    to complete the verification progress.
                  </p>
                </div>

                <div className="flex justify-center gap-4 mt-4">
                  <input
                    {...register("code")}
                    name="code"
                    type="text"
                    autoComplete="off"
                    placeholder="6-digit code"
                    maxLength={6}
                    className={classNames(
                      "w-full placeholder:text-base placeholder:font-poppins placeholder:font-normal font-sourceSansPro font-bold text-xl text-center bg-white block px-[15px] py-[12px] border border-primary-blue rounded-md focus:outline-none focus:ring-1 focus:ring-primary-blue",
                      {
                        "border-red-500 text-red-600 focus:border-red-500 focus:ring-red-500":
                          formState?.errors?.code,
                      }
                    )}
                  />
                  <button
                    className="items-center justify-center px-6 py-3 text-base font-semibold tracking-wider text-white border rounded-md shadow-sm border-primary-blue bg-primary-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                    type="submit"
                  >
                    CONFIRM
                  </button>
                </div>
                <div className="mt-1 text-sm text-red-500">
                  {formState?.errors?.code?.message}
                </div>
                <div className="flex justify-center mt-6">
                  <button
                    className="text-sm font-semibold transition-all text-primary-blue disabled:text-gray-400"
                    type="button"
                    onClick={resendCode}
                    disabled={timeLeft > 0}
                  >
                    Resend verification code
                  </button>
                  {timeLeft > 0 && (
                    <div className="w-8 ml-1 text-sm font-semibold text-primary-blue">
                      0:{timeLeft < 10 ? `0${timeLeft}` : timeLeft}
                    </div>
                  )}
                </div>
              </form>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
